import type { AgentTool, ToolContext } from './tools';

/** A single planning item. `in_progress` should mark exactly the one task currently being worked on. */
export interface TodoItem {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
}

const MARK: Record<TodoItem['status'], string> = {
  pending: '[ ]',
  in_progress: '[~]',
  completed: '[x]',
};

/** Render a todo list as a stable checklist (one item per line). */
function renderTodos(todos: TodoItem[]): string {
  if (todos.length === 0) return 'Todo list cleared (no items).';
  return todos.map((t) => `${MARK[t.status]} ${t.content}`).join('\n');
}

/**
 * `TodoWrite` tool — the agent's planning scratchpad for multi-step tasks.
 * Use to lay out steps up front and keep them current (mark one `in_progress`,
 * flip finished ones to `completed`). Each call REPLACES the whole list.
 */
export const todoWriteTool: AgentTool = {
  name: 'TodoWrite',
  description:
    'Maintain a todo list to plan and track a multi-step task. Replaces the current list with the provided todos and returns the rendered checklist. Mark exactly one item in_progress at a time; flip items to completed as you finish them.',
  parameters: {
    type: 'object',
    required: ['todos'],
    properties: {
      todos: {
        type: 'array',
        items: {
          type: 'object',
          required: ['content', 'status'],
          properties: {
            content: { type: 'string', description: 'the step to do' },
            status: { type: 'string', enum: ['pending', 'in_progress', 'completed'] },
          },
        },
      },
    },
  },
  async run({ todos }, ctx: ToolContext) {
    if (!Array.isArray(todos)) throw new Error('todos must be an array of { content, status } items.');
    const next: TodoItem[] = todos.map((t: any, i: number) => {
      const content = String(t?.content ?? '').trim();
      if (!content) throw new Error(`todos[${i}].content is required.`);
      const status = t?.status;
      if (status !== 'pending' && status !== 'in_progress' && status !== 'completed') {
        throw new Error(`todos[${i}].status must be one of pending|in_progress|completed (got ${JSON.stringify(status)}).`);
      }
      return { content, status };
    });
    ctx.todos = next; // replace the list in place on the shared context
    return renderTodos(next);
  },
};
